import { completeConfig } from '@/components/ui/data-table/config';
import { DataTable } from '@/components/ui/data-table/data-table';
import { DataTableConsumer } from '@/components/ui/data-table/types';
import { accountsTableConfig } from '@/config/table-configs';
import { getAccountActions } from '@/features/accounts/accounts.actions';
import { accountFields } from '@/features/accounts/accounts.fields';
import { useFilterableTable } from '@/hooks/useFilterableTable';
import { usePanelFilterState } from '@/hooks/usePanelFilterState';
import { usePanelSelectionState } from '@/hooks/usePanelSelectionState';
import { usePersistedTableConfig } from '@/hooks/usePersistedTableConfig';
import { ShareContent } from '@/utils/share';
import { AdminAccount } from '@certhon/models';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';
import { formatDistanceToNow } from 'date-fns';
import { ExpirationStatus } from './components/ExpirationStatus';
import { LoginNameTag } from './components/LoginNameTag';
import { PasswordStrengthIndicator } from './components/PasswordStrengthIndicator';

const emptyArray: AdminAccount[] = [];

function AuthAttemptsCell({ value }: { value: number }) {
  return (
    <span
      className={cn(
        value > 10
          ? 'text-destructive font-semibold'
          : value > 3
            ? 'text-warning'
            : '',
      )}
    >
      {value}
    </span>
  );
}

function LastLoginCell({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">Never</span>;
  return (
    <span>{formatDistanceToNow(new Date(value), { addSuffix: true })}</span>
  );
}

const customColumns: ColumnDef<AdminAccount>[] = [
  {
    id: 'loginname',
    header: 'Login',
    accessorKey: 'loginname',
    cell: ({ row }) => <LoginNameTag loginname={row.original.loginname} />,
  },
  {
    id: 'auth_attempts',
    header: 'Failed Attempts',
    accessorKey: 'auth_attempts',
    cell: ({ row }) => <AuthAttemptsCell value={row.original.auth_attempts} />,
  },
  {
    id: 'last_successful_login',
    header: 'Last Login',
    accessorKey: 'last_successful_login',
    cell: ({ row }) => (
      <LastLoginCell value={row.original.last_successful_login} />
    ),
  },
  {
    id: 'password_strength',
    header: 'Password',
    accessorKey: 'password_strength',
    cell: ({ row }) => (
      <PasswordStrengthIndicator
        strength={row.original.password_strength}
        compact
      />
    ),
  },
  {
    id: 'expires_on',
    header: 'Expires',
    accessorKey: 'expires_on',
    cell: ({ row }) => (
      <ExpirationStatus expiresOn={row.original.expires_on} compact />
    ),
  },
];
export function AccountsTable({
  uriPrefix = '',
  data,
  isLoading,
}: {
  uriPrefix?: string;
} & DataTableConsumer<AdminAccount>) {
  const { filterValue, setFilterValue } = usePanelFilterState();
  const { rowSelection, setRowSelection, clearSelection } =
    usePanelSelectionState();

  const prevFilterRef = useRef(filterValue);
  useEffect(() => {
    if (prevFilterRef.current !== filterValue) {
      clearSelection();
      prevFilterRef.current = filterValue;
    }
  }, [filterValue, clearSelection]);

  const {
    tokenDefinitions,
    filteredData,
    columns,
    isFiltering,
    resultCount,
    totalCount,
  } = useFilterableTable<AdminAccount>({
    fields: accountFields,
    data: data || emptyArray,
    filterValue,
    customColumns,
  });

  const completeDefaultConfig = useMemo(
    () => completeConfig(accountsTableConfig, columns),
    [columns],
  );

  const [tableConfig, setTableConfig] = usePersistedTableConfig(
    'accounts',
    completeDefaultConfig,
  );

  const createUriPath = useCallback(
    (account: AdminAccount): string =>
      [uriPrefix, '/accounts/', account.recnum].join(''),
    [uriPrefix],
  );

  const createShareContent = useCallback(
    (account: AdminAccount, url: string): ShareContent => ({
      title: `Account: ${account.name}`,
      text: `${account.name} (${account.loginname || account.email}, ${account.recnum})`,
      url,
    }),
    [],
  );

  const rowActions = useMemo(() => getAccountActions, []);

  return (
    <DataTable<AdminAccount, unknown>
      isLoading={isLoading}
      data={filteredData}
      columns={columns}
      config={tableConfig}
      onConfigChange={setTableConfig}
      createUriPath={createUriPath}
      createShareContent={createShareContent}
      filterValue={filterValue}
      onFilterChange={setFilterValue}
      tokenDefinitions={tokenDefinitions}
      isFiltering={isFiltering}
      resultCount={resultCount}
      totalCount={totalCount}
      entityName="accounts"
      rowSelection={rowSelection}
      onRowSelectionChange={setRowSelection}
      actions={rowActions}
    />
  );
}

export default AccountsTable;
